import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { toast } from '@/hooks/useToast';
import { tenantKey } from '@/lib/queryKeys';
import { useEncounterMutation } from './hooks';
import { identityCandidates, resolveIdentity } from './api';
import { errorInfo } from './ui';
import type { Encounter, PatientHit } from './types';

/**
 * Identity resolve (D3, §7). Two paths, and only two:
 *  * MERGE — the patient already has a record: the ED placeholder is merged
 *    into it through the existing patient merge. Never a rename of someone
 *    else's record. Shown side by side so the match is a deliberate decision.
 *  * NEW — no existing record: fill in the placeholder's details and, once a
 *    mobile and sex are known, mark it verified.
 * Available after departure too — identity often surfaces days later.
 */
export default function IdentityResolveDialog({ encounter, open, onClose }: { encounter: Encounter; open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<'merge' | 'update'>('merge');
  const [q, setQ] = useState('');
  const [target, setTarget] = useState<PatientHit | null>(null);
  const [f, setF] = useState({ name: '', gender: '', age: '', mobile: '', address: '', verify: false });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [confirmMerge, setConfirmMerge] = useState(false);
  const save = useEncounterMutation(encounter.id, (body: Parameters<typeof resolveIdentity>[1]) => resolveIdentity(encounter.id, body));

  useEffect(() => {
    if (open) {
      setMode('merge'); setQ(''); setTarget(null); setFields({});
      setF({ name: encounter.identity_status === 'unknown' ? '' : encounter.patient_name, gender: encounter.gender ?? '', age: String(encounter.age ?? encounter.approx_age_years ?? ''), mobile: encounter.mobile ?? '', address: '', verify: false });
    }
  }, [open, encounter]);

  const hits = useQuery({
    queryKey: tenantKey('er', 'identity-candidates', encounter.id, q.trim()),
    queryFn: () => identityCandidates(encounter.id, q.trim()),
    enabled: open && mode === 'merge' && q.trim().length >= 2,
  });

  const submit = async (mergeConfirmed = false) => {
    setFields({});
    try {
      if (mode === 'merge') {
        if (!target) return;
        // Confirmed in a dialog — window.confirm() is refused by embedded browsers.
        if (!mergeConfirmed) { setConfirmMerge(true); return; }
        await save.mutateAsync({ mode: 'merge', target_patient_id: target.id });
        toast({ title: `Identified as ${target.name}`, description: `${encounter.uhid} now points to ${target.uhid}.`, variant: 'success' });
      } else {
        await save.mutateAsync({
          mode: 'update', name: f.name || undefined, gender: f.gender || undefined, age: f.age ? Number(f.age) : undefined,
          mobile: f.mobile || undefined, address: f.address || undefined, verify: f.verify,
        });
        toast({ title: f.verify ? 'Identity verified' : 'Details saved (provisional)', variant: 'success' });
      }
      onClose();
    } catch (e) {
      const info = errorInfo(e);
      setFields(info.fields);
      toast({ title: 'Could not resolve identity', description: info.message, variant: 'danger' });
    }
  };

  const row = (label: string, a: string | number | null | undefined, b: string | number | null | undefined) => (
    <div className="contents">
      <div className="text-ink-muted">{label}</div><div>{a ?? '—'}</div><div className="font-semibold">{b ?? '—'}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="touch-form max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Resolve identity — {encounter.patient_name}</DialogTitle>
          <DialogDescription>{encounter.uhid} · currently {encounter.identity_status}</DialogDescription>
        </DialogHeader>
        <SegmentedControl
          value={mode}
          onChange={(v) => setMode(v)}
          options={[{ value: 'merge', label: 'Has an existing record' }, { value: 'update', label: 'New patient — fill in details' }]}
        />

        {mode === 'merge' ? (
          <div className="space-y-3">
            <FormField label="Find the existing record (UHID / mobile / name)">
              <Input value={q} onChange={(e) => { setQ(e.target.value); setTarget(null); }} autoFocus />
            </FormField>
            {!target && (hits.data?.length ?? 0) > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-border">
                {hits.data!.map((p) => (
                  <button key={p.id} type="button" onClick={() => setTarget(p)} className="flex w-full justify-between border-b border-border px-3 py-1.5 text-left text-[13px] last:border-b-0 hover:bg-muted">
                    <span>{p.name} <span className="text-ink-muted">· {p.uhid}</span></span>
                    <span className="text-[11px] text-ink-muted">{p.mobile ?? ''}{p.age != null ? ` · ${p.age}y` : ''}</span>
                  </button>
                ))}
              </div>
            )}
            {target && (
              <div className="grid grid-cols-[auto_1fr_1fr] gap-x-4 gap-y-1 rounded-lg border border-border p-3 text-[12px]">
                <div /><div className="font-semibold text-ink-muted">ED record</div><div className="font-semibold text-ink-muted">Existing record</div>
                {row('Name', encounter.patient_name, target.name)}
                {row('UHID', encounter.uhid, target.uhid)}
                {row('Sex', encounter.gender, target.gender)}
                {row('Age', encounter.age ?? (encounter.approx_age_years != null ? `~${encounter.approx_age_years}` : null), target.age)}
                {row('Mobile', encounter.mobile, target.mobile)}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Name" className="col-span-2" error={fields.name}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
            <FormField label="Sex" error={fields.gender}>
              <NativeSelect value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}>
                <option value="">—</option><option>Male</option><option>Female</option><option>Other</option><option>Unknown</option>
              </NativeSelect>
            </FormField>
            <FormField label="Age"><Input type="number" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} /></FormField>
            <FormField label="Mobile" error={fields.mobile}><Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></FormField>
            <FormField label="Address"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></FormField>
            <label className="col-span-2 flex items-center gap-2 text-[12px]">
              <Checkbox checked={f.verify} onCheckedChange={(v) => setF({ ...f, verify: v === true })} />
              Identity confirmed (ID seen / confirmed by relative) — mark as verified
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} disabled={mode === 'merge' && !target} onClick={() => submit()}>{mode === 'merge' ? 'Merge into this record' : 'Save details'}</Button>
        </div>
        <ConfirmDialog
          open={confirmMerge}
          onOpenChange={setConfirmMerge}
          title={target ? `Merge into ${target.name} (${target.uhid})?` : 'Merge records?'}
          description="The ED encounter, visit and documents will move to that record, and this ED record will point to it."
          confirmLabel="Merge records"
          onConfirm={() => { void submit(true); }}
        />
      </DialogContent>
    </Dialog>
  );
}
